<template>
  <NuxtLayout name="catalog">
    <div class="page-theme-group">
      <div class="page-container">

        <AtomsContainersCornerBorders>

          <!-- header with annotation -->

          <div class="flex flex-wrap gap-x-8 desktop:gap-x-20">
            <div class="flex-1 p-6 tablet:p-12">
              <MoleculesGlobalBreadcrumb :segments="entity.parents" class="!mt-0 !mb-6 uppercase"></MoleculesGlobalBreadcrumb>
              <h1 class="text-[2.5rem] font-bold leading-[1em] tracking-tight tablet:text-[3rem]">{{ mainHeader }}</h1>
              <div v-if="subHeader" class="mt-6 text-2xl font-bold tracking-normal">{{ subHeader }}</div>
              <div class="mt-6 text-justify" v-html="entity.annotation"></div>
            </div>

            <!-- theme image -->

            <div class="w-full aspect-[16/6] bg-cover bg-center desktop:w-1/2" :style="`background-image: url(${useShopZone().value.staticResourcesRoot}/img/groups/topic/${entity.id}_rg.jpg)`"></div>

          </div>
        </AtomsContainersCornerBorders>

        <!-- groups -->

        <div class="page-theme-group__groups">
          <AtomsTextLink v-for="group in entity.groups?.[0]?.groups.sort((a, b) => b.priority - a.priority)" :to="group.page.url" class="page-theme-group__groups__item" @mouseover="hoverGroup(group.id)">
            <AtomsImagesImage class="page-theme-group__groups__image" :src="`${useShopZone().value.staticResourcesRoot}/img/groups/tiles/${group.id}.png`" :alt="group.name" />
            <div class="text-xl leading-6 font-bold">{{ group.name }}</div>
            <div v-if="groupWareCounts[group.id] !== undefined" class="page-theme-group__groups__item__overlay">
              <div class="w-[50px] text-center">
                <div class="text-xl font-bold">{{ groupWareCounts[group.id] }}</div>
                <div class="text-xs">{{ $t('produktů', groupWareCounts[group.id]) }}</div>
              </div>
              <div class="text-xl leading-6 font-bold">
                {{ group.name }}
              </div>
            </div>
          </AtomsTextLink>
        </div>
      </div>

      <div class="py-4 bg-neutral-100 tablet:py-6 desktop:py-12">
        <div class="page-container">
          <div class="flex flex-wrap gap-4">

            <!-- video -->

            <div v-if="entity.youtubeVideos?.length" class="w-full desktop:w-[450px]">
              <AtomsContainersCornerBorders class="flex items-center w-full aspect-video overflow-hidden">
                <MoleculesBlocksVideoItem :title="entity.youtubeVideos[0].title" :youtube-id="entity.youtubeVideos[0].youtubeId" :thumbnails="entity.youtubeVideos[0].thumbnails" :is-our-channel="entity.youtubeVideos[0].isOurChannel" class="absolute w-full" />
              </AtomsContainersCornerBorders>
              <div class="flex-1 mt-2 p-4 text-neutral-50 bg-neutral-500 desktop:p-6 before:content-[''] before:block before:w-[50px] before:mb-4 before:border-t-2 before:border-secondary-500">
                {{ entity.youtubeVideos[0].title }}
              </div>
            </div>

            <!-- description -->

            <div class="flex-1 flex items-center p-4 bg-neutral-50 tablet:p-6 desktop:p-8">
              <div v-html="entity.description"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="page-container">
        <div class="space-y-8">

          <!-- articles -->

          <div>
            <AtomsTextHeading class="mt-8 mb-4">{{ $t('Nevíte si rady?') }}</AtomsTextHeading>
            <div class="flex flex-wrap gap-4 mt-6 tracking-normal">
              <MoleculesBlocksMagazineItem v-for="article in articles" :date-published="article.page.publishedAt" :date-created="article.created" :title="article.page.title" :url="article.page.url" :annotation="article.annotation" :time-to-read="article.timeToRead" :item-id="article.id" class="w-full !flex-none tablet:!flex-1" />
            </div>
            <div class="mt-6 text-right">
              <MoleculesButtonsButton :href="`${cache.getModuleUrl('modules/magazine')}#l=${entity.infoPageFilter.flatMap(item => item.lValue).join(',')}`" :black="true" :arrow-right="true">{{ $t('Zobrazit všechny tematické články') }}</MoleculesButtonsButton>
            </div>
          </div>

          <!-- recently viewed items -->

          <LazyTemplatesRecentlyViewedItems />

          <!-- usp -->

          <OrganismsBlocksUsps class="!mt-0 mb-4 desktop:mb-6" item-class="!bg-neutral-100 desktop:aspect-[7/3]" />

        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
<script setup>

const page = usePage().value.page
const entity = useEntity();
const locale = useLocale().getLocale();
const cache = useCache();
const appConfig = useAppConfig();

const indexOfFirstHyphen = page.header.indexOf('-');
const mainHeader = indexOfFirstHyphen > -1 ? page.header.substring(0, indexOfFirstHyphen) : page.header;
const subHeader = indexOfFirstHyphen > -1 ? page.header.substring(indexOfFirstHyphen + 1) : '';

const articles = (await useApiFetch(`/api/magazineArticlesDefault?currencyId=${locale.currencyId}&languageId=${locale.languageId}&labelLValues=${entity.infoPageFilter.map(q => q.lValue).join(',')}`)).infoPageList.infoPages.items.filter(q => q.page.publishedAt);

const groupWareCounts = reactive({});

const hoverGroup = async (groupId) => {

  if (groupWareCounts[groupId] === undefined) {
    const result = await getWareCountForGroup(groupId);
    groupWareCounts[groupId] = result;
  }

  return groupWareCounts[groupId];
}

const getWareCountForGroup = async (groupId) => {

  const result = (await useApiFetch(`/api/groupWareCount?groupId=${groupId}&currencyId=${locale.currencyId}&languageId=${locale.languageId}`))?.groups?.[0]?.wareCount;

  return result;
}

</script>
<style lang="postcss">
.page-theme-group {
  @apply pt-4 pb-8 tablet:pt-8 desktop:pt-16;

  &__groups {

    @apply grid gap-4 mt-8 mb-6 tracking-normal desktop:mb-12;

    grid-template-columns: repeat(auto-fill, minmax(240px, 6fr));

    &__item {
      @apply relative flex items-center gap-4 p-4 border border-neutral-500 hover:no-underline;

      &__overlay {
        @apply absolute left-0 top-0 right-0 bottom-0 flex items-center gap-4 p-4 bg-neutral-500 text-neutral-50 pointer-events-none opacity-0 transition-all duration-300;
      }

      &:hover &__overlay {
        @apply opacity-100;
      }

    }

    &__image {
      @apply w-[45px] h-[45px] m-[2.5px] object-contain object-center;
    }
  }
}
</style>